import jsonToFormData from 'json-form-data'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy, Input } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription } from 'rxjs'
import { getUpdateBusinessState } from '@containers/businesses/businesses.selectors'
import {
  DeleteBusinessDocumentRequest,
  DeleteDocumentRequest,
  DownloadBusinessDocumentRequest,
  FetchBusinessDocumentsRequest,
  GenerateBusinessDocumentRequest,
  UploadDocumentRequest,
} from '@containers/businesses/businesses.actions'
import { fromJS, List } from 'immutable'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { FetchDocumentTypesRequest } from '@shared/shared.actions'
import { getDocumentTypesState } from '@shared/shared.select'
import { NgxPermissionsService } from 'ngx-permissions'
import { FormControl, FormGroup, Validators } from '@angular/forms'

enum DATA_CYCLE_FILTER {
  Current,
  All,
}
@Component({
  selector: 'documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.css'],
})
export class DocumentsComponent extends FormTrait implements OnInit, OnDestroy {
  @Input() isReadonly: boolean = false
  public businessId: number
  public documents: any = fromJS([])
  private state$: Subscription
  public isLoading: boolean
  public isGenerating: boolean
  public isDeleting: boolean
  public licenses: any = fromJS([])
  public isUploadModalOpen: boolean
  public uploadForm: FormGroup
  public licensesColumns = [
    {
      prop: 'referenceId',
      name: 'REFERENCE_ID',
    },
    {
      prop: 'dataCycle',
      name: 'YEAR',
      render: (row: any) => row.get('year'),
    },
    {
      prop: 'fileName',
      name: 'DOCUMENT_NAME',
    },
    {
      prop: 'generatedOn',
      name: 'GENERATED_AT',
      template: 'generatedOn',
    },
    {
      prop: 'download',
      template: 'actionDownload',
    },
  ]
  public columns = [
    {
      prop: 'description',
      name: 'DESCRIPTION',
    },
    {
      prop: 'fileName',
      name: 'DOCUMENT_NAME',
    },
    {
      prop: 'createdAt',
      name: 'CREATED_AT',
      template: 'createdAt',
    },
    {
      prop: 'actions',
      template: 'actions',
    },
  ]

  public radioOptions = [
    {
      label: 'CURRENT_ONLY',
      value: DATA_CYCLE_FILTER.Current,
    },
    {
      label: 'ALL_RECORDS',
      value: DATA_CYCLE_FILTER.All,
    },
  ]

  public dataCycle: DATA_CYCLE_FILTER = DATA_CYCLE_FILTER.Current
  public currentDataCycleId: any
  public settings$: Subscription
  public documentTypes: Map<string, any>
  private documentTypes$: Subscription
  public activeTab = 0
  public hasReadonlyPermission: boolean = false

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    public permissionsService: NgxPermissionsService,
  ) {
    super()
  }

  ngOnInit() {
    this.businessId = parseInt(this.route.snapshot.paramMap.get('id'))
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined

    this.uploadForm = new FormGroup({
      description: new FormControl('', [Validators.required]),
      typeId: new FormControl(),
      file: new FormControl('', [Validators.required]),
    })

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((settings) => {
        this.currentDataCycleId = settings.getIn([
          'data',
          'system',
          'SYSTEM_ACTIVE_DATA_CYCLE',
          'id',
        ])
      })

    this.state$ = this.store
      .pipe(select(getUpdateBusinessState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.getIn(['documents', 'isLoading'])
        this.isGenerating = state.getIn([
          'documents',
          'generateDocumentLoading',
        ])
        this.isDeleting = state.getIn(['documents', 'deleteDocumentLoading'])

        this.documents = state.getIn(['documents', 'data', 'documents'])

        this.licenses = this.filterDocuments(
          state.getIn(['documents', 'data', 'licenses']),
        )
      })

    this.documentTypes$ = this.store
      .pipe(select(getDocumentTypesState))
      .subscribe((state) => {
        this.documentTypes = state
      })

    this.store.dispatch<any>(new FetchBusinessDocumentsRequest(this.businessId))
    this.store.dispatch<any>(new FetchDocumentTypesRequest())
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.settings$.unsubscribe()
    this.documentTypes$.unsubscribe()
  }

  public dataCycleChange(value) {
    this.dataCycle = value

    // this.documents = this.filterDocuments(
    //   this.state.getIn(['documents', 'data']),
    // )

    this.licenses = this.filterDocuments(
      this.state.getIn(['documents', 'data', 'licenses']),
    )
  }

  public filterDocuments(documents: any = []) {
    return documents.filter((document) =>
      this.dataCycle === DATA_CYCLE_FILTER.Current
        ? document.getIn(['dataCycle', 'id']) === this.currentDataCycleId
        : true,
    )
  }

  public handleTabChange(tab: any) {
    this.activeTab = tab
  }

  public download(document: any) {
    this.store.dispatch(
      new DownloadBusinessDocumentRequest({
        businessId: this.businessId,
        dataCycleId: document.getIn(['dataCycle', 'id']),
        id: document.get('id'),
      }),
    )
  }

  public generate(document: any) {
    this.store.dispatch(
      new GenerateBusinessDocumentRequest({
        businessId: this.businessId,
        dataCycleId: document.getIn(['dataCycle', 'id']),
      }),
    )
  }

  public delete(document: any) {
    if (document.get('id')) {
      this.store.dispatch(new DeleteDocumentRequest(document.get('id')))
    } else if (document.get('businessId')) {
      this.store.dispatch(
        new DeleteBusinessDocumentRequest(document.get('businessId')),
      )
    }
  }

  public get isCurrentCycle() {
    return this.dataCycle === DATA_CYCLE_FILTER.Current
  }

  public getDocumentsByType(typeId: string): List<Map<string, any>> {
    return (this.documents as any).filter(
      (document: Map<string, any>) => document.get('typeId') === typeId,
    )
  }

  public toggleModal() {
    this.isUploadModalOpen = !this.isUploadModalOpen
  }

  public uploadDocument(typeId: number) {
    this.uploadForm.get('typeId').setValue(typeId)
    this.toggleModal()
  }

  public upload() {
    if (this.uploadForm.valid) {
      this.store.dispatch<any>(
        new UploadDocumentRequest(
          this.businessId,
          jsonToFormData(this.uploadForm.value),
        ),
      )
      this.toggleModal()
      this.uploadForm.reset()
    } else {
      this.uploadForm.markAllAsTouched()
    }
  }

  public documentChange(event: any) {
    this.uploadForm.get('file').setValue(event.target.files[0])
  }
}
